import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from './ToastContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// 管理员账号配置
const ADMIN_CONFIG = {
  username: 'admin',
  password: 'admin123',
  isAdmin: true
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { error: showError } = useToast();

  // 初始化时检查本地存储
  useEffect(() => {
    const initAuth = () => {
      try {
        const savedUser = localStorage.getItem('loveGameUser');
        if (savedUser) {
          const parsed = JSON.parse(savedUser);
          // 检查是否过期（7天）
          const now = Date.now();
          const expiry = parsed.timestamp + 7 * 24 * 60 * 60 * 1000;
          
          if (now < expiry) {
            setUser(parsed);
            setIsAdmin(parsed.isAdmin || false);
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('loveGameUser');
          }
        }
      } catch (e) {
        console.error('Auth initialization error:', e);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      setIsLoading(true);

      // 验证输入
      if (!username?.trim() || !password?.trim()) {
        return { 
          success: false, 
          error: '请填写用户名和密码'
        };
      }

      // 管理员登录
      if (username === ADMIN_CONFIG.username && password === ADMIN_CONFIG.password) {
        const adminUser = { 
          username: ADMIN_CONFIG.username, 
          isAdmin: true,
          timestamp: Date.now()
        };
        setUser(adminUser);
        setIsAdmin(true);
        setIsAuthenticated(true);
        localStorage.setItem('loveGameUser', JSON.stringify(adminUser));
        return { success: true, isAdmin: true };
      }
      
      // 普通用户登录 - 查询数据库验证
      const { data: users, error } = await supabase
        .from('registered_users')
        .select('*')
        .eq('username', username.trim())
        .eq('password_hash', password);

      if (error) {
        console.error('Login query error:', error);
        return { 
          success: false, 
          error: '登录失败，请重试'
        };
      }

      if (users && users.length > 0) {
        const normalUser = { 
          username: users[0].username, 
          isAdmin: false,
          timestamp: Date.now()
        };
        setUser(normalUser);
        setIsAdmin(false);
        setIsAuthenticated(true);
        localStorage.setItem('loveGameUser', JSON.stringify(normalUser));
        return { success: true, isAdmin: false };
      }

      // 验证失败
      return { 
        success: false, 
        error: '用户名或密码错误'
      };
    } catch (err) {
      console.error('Login error:', err);
      return { 
        success: false, 
        error: '登录失败，请重试'
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (username, password) => {
    try {
      setIsLoading(true);
      console.log('Registering user:', username);
      
      if (username.trim().length < 2 || password.length < 4) {
        return { 
          success: false, 
          error: '用户名至少需要2个字符，密码至少需要4个字符'
        };
      }

      // 检查用户名是否已存在
      const { data: existingUsers, error: checkError } = await supabase
        .from('registered_users')
        .select('username')
        .eq('username', username.trim());

      console.log('Check existing:', { existingUsers, checkError });

      if (existingUsers && existingUsers.length > 0) {
        return { success: false, error: '用户名已存在' };
      }

      // 插入新用户到数据库
      const { data: insertData, error: insertError } = await supabase
        .from('registered_users')
        .insert({
          username: username.trim(),
          password_hash: password,
          is_admin: false
        })
        .select();

      console.log('Insert result:', { insertData, insertError });

      if (insertError) {
        console.error('Insert error:', insertError);
        return { success: false, error: `注册失败：${  insertError.message}` };
      }

      return { success: true, message: '注册成功，请登录' };
    } catch (err) {
      console.error('Register error:', err);
      return { 
        success: false, 
        error: `注册失败：${  err.message}`
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setIsAdmin(false);
    setIsAuthenticated(false);
    localStorage.removeItem('loveGameUser');
  }, []);

  const value = {
    user,
    isAdmin,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
